import { Button } from "@mui/material";
import { Form, Modal, FloatingLabel, Alert } from "react-bootstrap";
import { useForm } from "hooks/useForm";
import { toast } from "react-toastify";
import { useOrderContext } from "context/OrderContext";
import { useEditCustomerDetailsMutation } from "app/api/orders";

export interface EditCustomerFormFields {
  name: string;
  address: string;
  postAddress: string;
  postalCode: string;
}

const EditCustomerData = ({ show, setShow }) => {
  const order = useOrderContext();
  const [editCustomerDetails, { isLoading }] = useEditCustomerDetailsMutation();

  const submitCustomerDetails = () => {
    editCustomerDetails({ orderId: order.ordernr, ...customerData })
      .unwrap()
      .then(() => {
        setShow(false);
        toast.success("Kunduppgifterna har redigerats!", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      })
      .catch(() => {
        toast.error("Något gick fel, försök igen", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      });
  };

  const {
    handleSubmit,
    handleChange,
    data: customerData,
    errors,
  } = useForm<EditCustomerFormFields>({
    validations: {
      name: {
        custom: {
          isValid: (value) => {
            return value.length > 0;
          },
          message: "Ange kundens namn",
        },
      },
      address: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange adress",
        },
      },
      postAddress: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange ort",
        },
      },
      postalCode: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange postnummer",
        },
      },
    },
    initialValues: {
      name: order?.companyName,
      address: order?.address,
      postAddress: order?.postaddress,
      postalCode: order?.postnr,
    },
    onSubmit: submitCustomerDetails,
  });

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)} animation={true}>
        <Modal.Header closeButton>
          <Modal.Title>Redigera kunduppgifter</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <FloatingLabel label="Kund" className="mb-3">
              <Form.Control
                type="text"
                onChange={handleChange("name")}
                className={`form-control mb-3 ${errors.name && "is-invalid"}`}
                value={customerData.name}
              />
            </FloatingLabel>
            <FloatingLabel label="Adress" className="mb-3">
              <Form.Control
                type="text"
                onChange={handleChange("address")}
                className={`form-control mb-3 ${errors.address && "is-invalid"}`}
                value={customerData.address}
              />
            </FloatingLabel>
            <FloatingLabel label="Postnummer" className="mb-3">
              <Form.Control
                type="text"
                onChange={handleChange("postalCode")}
                className={`form-control mb-3 ${errors.postalCode && "is-invalid"}`}
                value={customerData.postalCode}
              />
            </FloatingLabel>
            <FloatingLabel label="Ort" className="mb-3">
              <Form.Control
                type="text"
                onChange={handleChange("postAddress")}
                className={`form-control mb-3 ${errors.postAddress && "is-invalid"}`}
                value={customerData.postAddress}
              />
            </FloatingLabel>
            {Object.keys(errors).length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0">
                  {Object.keys(errors).map((field) => {
                    const errorValue = errors[field];

                    return (
                      <li className="m-0" key={field}>
                        {errorValue}
                      </li>
                    );
                  })}
                </ul>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              color="error"
              variant="contained"
              className="me-2"
              disabled={isLoading}
              onClick={() => setShow(false)}
            >
              Avbryt
            </Button>
            <Button variant="contained" type="submit" loading={isLoading}>
              Spara
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default EditCustomerData;
